/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { 
    PanelBody, 
    TextControl, 
    TextareaControl,
    RangeControl,
    ToggleControl,
    PanelColorSettings,
    SelectControl
} from '@wordpress/components';
import { 
    InspectorControls, 
    useBlockProps,
    __experimentalColorGradientSettingsDropdown as ColorGradientSettingsDropdown,
    __experimentalUseMultipleOriginColorsAndGradients as useMultipleOriginColorsAndGradients,
} from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';

/**
 * Edit function for the blog listing block
 */
export default function Edit({ attributes, setAttributes }) {
    const {
        title,
        description,
        postsToShow,
        showExcerpts,
        showDate,
        linkText,
        linkUrl,
        backgroundColor,
        textColor,
        postType,
        enablePagination,
        postsPerPage,
        displayMode,
        enableFiltering,
        filterType,
        filterTerms,
        filterTerm,
        theme,
        excludeCurrentPost,
        unclickableLinks
    } = attributes;

    // Get available post types and categories dynamically based on selected post type
    const { postTypes, categories } = useSelect((select) => {
        const { getPostTypes, getEntityRecords } = select('core');
        
        // Get all taxonomies for the selected post type
        const postTypesData = getPostTypes({ per_page: -1 }) || [];
        const currentPostType = postTypesData.find(pt => pt.slug === postType);
        
        let categories = [];
        
        if (currentPostType && currentPostType.taxonomies) {
            // Try to get categories from the first available taxonomy
            const firstTaxonomy = currentPostType.taxonomies[0];
            if (firstTaxonomy) {
                categories = getEntityRecords('taxonomy', firstTaxonomy, { per_page: -1 }) || [];
            }
        } else if (postType === 'post' || postType === 'auto') {
            // Fallback for regular posts
            categories = getEntityRecords('taxonomy', 'category', { per_page: -1 }) || [];
        }
        
        return {
            postTypes: postTypesData,
            categories: categories
        };
    }, [postType]); // Re-run when postType changes

    // Build post type options
    const postTypeOptions = [
        { value: 'auto', label: __('Auto (Blog then Posts)', 'puroclean') },
        { value: 'post', label: __('Posts', 'puroclean') }
    ];

    // Add custom post types
    if (postTypes) {
        postTypes.forEach((postType) => {
            if (postType.viewable && postType.slug !== 'post' && postType.slug !== 'page') {
                postTypeOptions.push({
                    value: postType.slug,
                    label: postType.labels.name || postType.slug
                });
            }
        });
    }

    // Build category options based on selected post type
    const categoryOptions = [
        { value: '', label: __('All Categories', 'puroclean') }
    ];

    if (categories && categories.length > 0) {
        categories.forEach((category) => {
            categoryOptions.push({
                value: category.slug,
                label: category.name
            });
        });
    } else if (postType !== 'post' && postType !== 'auto') {
        // If no categories found for custom post type, show a helpful message
        categoryOptions.push({
            value: '',
            label: __('No categories found for this post type', 'puroclean')
        });
    }

    const blockProps = useBlockProps({
        className: 'blog-listing-block-editor',
        style: {
            backgroundColor: backgroundColor || undefined,
            color: textColor || undefined,
        }
    });

    const colorSettings = useMultipleOriginColorsAndGradients();

    return (
        <>
            <InspectorControls>
                <PanelBody title={__('Content Settings', 'puroclean')} initialOpen={true}>
                    <TextControl
                        label={__('Title', 'puroclean')}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                        help={__('Main section title', 'puroclean')}
                    />
                    <TextareaControl
                        label={__('Description', 'puroclean')}
                        value={description}
                        onChange={(value) => setAttributes({ description: value })}
                        help={__('Main section description', 'puroclean')}
                        rows={4}
                    />
                </PanelBody>

                <PanelBody title={__('Display Settings', 'puroclean')} initialOpen={true}>
                    <SelectControl
                        label={__('Theme', 'puroclean')}
                        value={theme}
                        onChange={(value) => setAttributes({ theme: value })}
                        options={[
                            { value: '', label: __('Default', 'puroclean') },
                            { value: 'minimal', label: __('Minimal', 'puroclean') },
                        ]}
                        help={__('Choose the visual theme for the blog listing', 'puroclean')}
                    />
                    <RangeControl
                        label={__('Number of Posts', 'puroclean')}
                        value={postsToShow}
                        onChange={(value) => setAttributes({ postsToShow: value })}
                        min={1}
                        max={12}
                        help={__('How many blog posts to display', 'puroclean')}
                    />
                    <ToggleControl
                        label={__('Show Excerpts', 'puroclean')}
                        checked={showExcerpts}
                        onChange={(value) => setAttributes({ showExcerpts: value })}
                        help={__('Display post excerpts in the cards', 'puroclean')}
                    />
                    <ToggleControl
                        label={__('Show Date', 'puroclean')}
                        checked={showDate}
                        onChange={(value) => setAttributes({ showDate: value })}
                        help={__('Display publication date for each post', 'puroclean')}
                    />
                    <ToggleControl
                        label={__('Exclude Current Post', 'puroclean')}
                        checked={excludeCurrentPost}
                        onChange={(value) => setAttributes({ excludeCurrentPost: value })}
                        help={__('Exclude the current post from the listing (useful for related posts)', 'puroclean')}
                    />
                    <ToggleControl
                        label={__('Make Links Unclickable', 'puroclean')}
                        checked={unclickableLinks}
                        onChange={(value) => setAttributes({ unclickableLinks: value })}
                        help={__('Remove clickable links from post titles and images (useful for CPTs without single pages)', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Content Source', 'puroclean')} initialOpen={true}>
                    <SelectControl
                        label={__('Post Type', 'puroclean')}
                        value={postType}
                        onChange={(value) => setAttributes({ postType: value })}
                        options={postTypeOptions}
                        help={__('Choose which type of content to display', 'puroclean')}
                    />
                    <SelectControl
                        label={__('Display Mode', 'puroclean')}
                        value={displayMode}
                        onChange={(value) => setAttributes({ displayMode: value })}
                        options={[
                            { value: 'cards', label: __('Card Layout (Mobile Swipeable)', 'puroclean') },
                            { value: 'grid', label: __('Grid Layout (All Devices)', 'puroclean') },
                        ]}
                        help={__('Choose how posts are displayed', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Filter Settings', 'puroclean')} initialOpen={false}>
                    <ToggleControl
                        label={__('Enable Category Filtering', 'puroclean')}
                        checked={enableFiltering}
                        onChange={(value) => setAttributes({ enableFiltering: value })}
                        help={__('Filter posts by a specific category', 'puroclean')}
                    />
                    {enableFiltering && (
                        <SelectControl
                            label={__('Category', 'puroclean')}
                            value={filterTerm}
                            onChange={(value) => setAttributes({ filterTerm: value })}
                            options={categoryOptions}
                            help={__('Choose a category to filter posts', 'puroclean')}
                        />
                    )}
                </PanelBody>

                <PanelBody title={__('Pagination Settings', 'puroclean')} initialOpen={false}>
                    <ToggleControl
                        label={__('Enable Pagination', 'puroclean')}
                        checked={enablePagination}
                        onChange={(value) => setAttributes({ enablePagination: value })}
                        help={__('Enable pagination to show more posts across multiple pages', 'puroclean')}
                    />
                    {enablePagination && (
                        <RangeControl
                            label={__('Posts Per Page', 'puroclean')}
                            value={postsPerPage}
                            onChange={(value) => setAttributes({ postsPerPage: value })}
                            min={1}
                            max={20}
                            help={__('Number of posts to show per page when pagination is enabled', 'puroclean')}
                        />
                    )}
                </PanelBody>

                <PanelBody title={__('Link Settings', 'puroclean')} initialOpen={false}>
                    <TextControl
                        label={__('Link Text', 'puroclean')}
                        value={linkText}
                        onChange={(value) => setAttributes({ linkText: value })}
                        help={__('Text for the "explore more" link', 'puroclean')}
                    />
                    <TextControl
                        label={__('Link URL', 'puroclean')}
                        value={linkUrl}
                        onChange={(value) => setAttributes({ linkUrl: value })}
                        help={__('URL for the "explore more" link', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Colors', 'puroclean')} initialOpen={false}>
                    <ColorGradientSettingsDropdown
                        __experimentalIsRenderedInSidebar
                        settings={[
                            {
                                colorValue: backgroundColor,
                                onColorChange: (value) => setAttributes({ backgroundColor: value }),
                                label: __('Background Color', 'puroclean'),
                            },
                            {
                                colorValue: textColor,
                                onColorChange: (value) => setAttributes({ textColor: value }),
                                label: __('Text Color', 'puroclean'),
                            },
                        ]}
                        panelId="blog-listing-colors"
                        {...colorSettings}
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                <div className="py-8 border-2 border-dashed border-gray-300">
                    <div className="max-w-content mx-auto px-4">
                        
                        {/* Header Section */}
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">
                                {title || __('Latest Water Damage Restoration Articles', 'puroclean')}
                            </h2>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                {description || __('Stay informed with our expert insights...', 'puroclean')}
                            </p>
                        </div>

                        {/* Mobile Swipeable Blog Posts (Single Post View) - Only for Cards Mode */}
                        {displayMode === 'cards' && (
                            <div className="mb-8 block lg:hidden">
                            <div className="relative overflow-hidden rounded-lg">
                                {/* Mobile Blog Slider */}
                                <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-smooth motion-reduce:scroll-auto touch-pan-x" 
                                     style={{userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none'}}>
                                    
                                    {[...Array(Math.min(enablePagination ? postsPerPage : postsToShow, 6))].map((_, index) => (
                                        <div key={index} className="flex-shrink-0 w-full snap-start">
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm mx-2">
                                                {/* Mock Featured Image */}
                                                <div className="bg-gray-300 h-48 flex items-center justify-center">
                                                    <span className="text-gray-500 text-sm">📷 Featured Image</span>
                                                </div>
                                                
                                                {/* Post Content */}
                                                <div className="p-4">
                                                    {showDate && (
                                                        <div className="text-xs text-gray-500 mb-2">December {15 + index}, 2024</div>
                                                    )}
                                                    
                                                    <h3 className="text-lg font-bold mb-2 text-gray-800">
                                                        {unclickableLinks ? (
                                                            `Sample Blog Post Title ${index + 1}`
                                                        ) : (
                                                            <a href="#" className="text-gray-800 hover:text-blue-600">
                                                                Sample Blog Post Title {index + 1}
                                                            </a>
                                                        )}
                                                    </h3>
                                                    
                                                    {showExcerpts && (
                                                        <p className="text-gray-600 text-sm mb-3">
                                                            This is a sample excerpt for blog post {index + 1}. It provides a brief overview of the content...
                                                        </p>
                                                    )}
                                                    
                                                    {/* Read More Link */}
                                                    <div className="text-sm text-blue-600 underline flex items-center gap-1">
                                                        → Read More
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Mobile Swipe Indicator */}
                                {(enablePagination ? postsPerPage : postsToShow) > 1 && (
                                    <div className="flex justify-center items-center p-3 absolute bottom-0 left-0 right-0 bg-black/40 rounded-b-lg pointer-events-none">
                                        <span className="text-white text-sm font-medium">Swipe for more</span>
                                        <span className="ml-2 text-white">→</span>
                                    </div>
                                )}

                                {/* Mobile Dots Navigation */}
                                {(enablePagination ? postsPerPage : postsToShow) > 1 && (
                                    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 bg-black/30 rounded-full px-3 py-2">
                                        {[...Array(Math.min(enablePagination ? postsPerPage : postsToShow, 6))].map((_, index) => (
                                            <button key={index} className="w-2 h-2 rounded-full bg-white/60 hover:bg-white transition-colors" 
                                                    aria-label={`Go to post ${index + 1}`}></button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </div>
                        )}

                        {/* Blog Posts Grid - Desktop for Cards Mode, All Devices for Grid Mode */}
                        {displayMode === 'cards' ? (
                            <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {[...Array(Math.min(enablePagination ? postsPerPage : postsToShow, 6))].map((_, index) => (
                                    <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                        {/* Mock Featured Image */}
                                        <div className="bg-gray-300 h-32 flex items-center justify-center">
                                            <span className="text-gray-500 text-sm">📷 Featured Image</span>
                                        </div>
                                        
                                        {/* Post Content */}
                                        <div className="p-4">
                                            {showDate && (
                                                <div className="text-xs text-gray-500 mb-2">December {15 + index}, 2024</div>
                                            )}
                                            
                                            <h3 className="text-lg font-bold mb-2 text-gray-800">
                                                {unclickableLinks ? (
                                                    `Sample Blog Post Title ${index + 1}`
                                                ) : (
                                                    <a href="#" className="text-gray-800 hover:text-blue-600">
                                                        Sample Blog Post Title {index + 1}
                                                    </a>
                                                )}
                                            </h3>
                                            
                                            {showExcerpts && (
                                                <p className="text-gray-600 text-sm mb-3">
                                                    This is a sample excerpt for blog post {index + 1}. It provides a brief overview of the content...
                                                </p>
                                            )}
                                            
                                            {/* Read More Link */}
                                            <div className="text-sm text-blue-600 underline flex items-center gap-1">
                                                → Read More
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {[...Array(Math.min(enablePagination ? postsPerPage : postsToShow, 6))].map((_, index) => (
                                    <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                        {/* Mock Featured Image */}
                                        <div className="bg-gray-300 h-32 flex items-center justify-center">
                                            <span className="text-gray-500 text-sm">📷 Featured Image</span>
                                        </div>
                                        
                                        {/* Post Content */}
                                        <div className="p-4">
                                            {showDate && (
                                                <div className="text-xs text-gray-500 mb-2">December {15 + index}, 2024</div>
                                            )}
                                            
                                            <h3 className="text-lg font-bold mb-2 text-gray-800">
                                                {unclickableLinks ? (
                                                    `Sample Blog Post Title ${index + 1}`
                                                ) : (
                                                    <a href="#" className="text-gray-800 hover:text-blue-600">
                                                        Sample Blog Post Title {index + 1}
                                                    </a>
                                                )}
                                            </h3>
                                            
                                            {showExcerpts && (
                                                <p className="text-gray-600 text-sm mb-3">
                                                    This is a sample excerpt for blog post {index + 1}. It provides a brief overview of the content...
                                                </p>
                                            )}
                                            
                                            {/* Read More Link */}
                                            <div className="text-sm text-blue-600 underline flex items-center gap-1">
                                                → Read More
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Explore More Button */}
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg">
                                → {linkText || __('Explore More Restoration Insights', 'puroclean')}
                            </div>
                        </div>

                        <div className="mt-6 text-center text-sm text-gray-500">
                            {__('✨ Blog Listing Block', 'puroclean')} - 
                            {postType === 'auto' ? __(' Auto (Blog/Posts)', 'puroclean') : 
                             postType === 'success_stories' ? __(' Success Stories', 'puroclean') :
                             postType === 'disasters' ? __(' Disasters', 'puroclean') :
                             __(` ${postType.charAt(0).toUpperCase() + postType.slice(1)}`, 'puroclean')} - 
                            {displayMode === 'cards' ? __(' Card Layout', 'puroclean') : __(' Grid Layout', 'puroclean')}
                            {enablePagination ? __(` - Paginated (${postsPerPage}/page)`, 'puroclean') : __(` - Limited (${postsToShow})`, 'puroclean')}
                            {enableFiltering && (filterTerm || (filterTerms && filterTerms.length > 0)) ? 
                                filterTerm ? 
                                    __(` - Filtered by category: ${filterTerm}`, 'puroclean') : 
                                    __(` - Filtered by ${filterType} (${filterTerms.length})`, 'puroclean') : ''}
                            {theme ? __(` - ${theme.charAt(0).toUpperCase() + theme.slice(1)} Theme`, 'puroclean') : ''}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
} 
